/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Button,
  Spinner,
  Radio,
  CardTable as IdentityListTable
} from '../../components'

class IdentitySearch extends React.Component {
  static propTypes = {
    searchText: PropTypes.string.isRequired,
    choose: PropTypes.bool,
    onAddNew: PropTypes.func,
    onClick: PropTypes.func
  }

  constructor(props) {
    super(props)

    this.state = {
      onFirstPage: true,
      onLastPage: false,
      identity: null
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // if componenent's loading prop changes from 'true' to 'false',
    if (this.props.loading && !nextProps.loading) {
      // and there is no next page, we're on the first AND last results page
      if (!nextProps.identities.pageInfo.hasNextPage) {
        this.setState({
          onLastPage: true,
          onFirstPage: true
        })
      } else {  // else we're on the first results page
        this.setState({
          onLastPage: false,
          onFirstPage: true
        })
      }
    }
  }

  // *** Note:
  // hasPreviousPage is only meaningful when last is included, as it is always
  // false otherwise. hasNextPage is only meaningful when first is included,
  // as it is always false otherwise.

  handleNextPage = () => {
    if (this.props.identities.pageInfo.hasNextPage) {
      // going forward, has next
      this.props.loadNextClients(this.props.searchText)
      .then((responseObj) => {
        if (!responseObj.data.identities.pageInfo.hasNextPage) {
          this.setState({
            onLastPage: true,
            onFirstPage: false // not guaranteed; might very well be on first page
          })
        }
        else {
          this.setState({ onFirstPage: false })
        }
      })
    } else {
      // changing direction: now going forward
      this.props.loadNextClients(this.props.searchText)
      .then((responseObj) => {
        if (!responseObj.data.identities.pageInfo.hasNextPage) {
            this.setState({
              onLastPage: true,
              onFirstPage: false, // not guaranteed; might be
            })
        }
        else {
          this.setState({ onFirstPage: false })
        }
      })
    }
  }

  handlePreviousPage = () => {
    if (this.props.identities.pageInfo.hasPreviousPage) {
      // going backward, has previous
      this.props.loadPreviousClients(this.props.searchText)
      .then((responseObj) => {
        if (!responseObj.data.identities.pageInfo.hasPreviousPage) {
          this.setState({
            onFirstPage: true,
            onLastPage: false  // not guaranteed; might be...
          })
        }
        else {
          this.setState({ onLastPage: false })
        }
      })
    } else {
      // changing direction: now going backward
      this.props.loadPreviousClients(this.props.searchText)
      .then((responseObj) => {
        if (!responseObj.data.identities.pageInfo.hasPreviousPage) {
          this.setState({
            onFirstPage: true,
            onLastPage: false  // not guaranteed; might be...
          })
        }
        else {
          this.setState({ onLastPage: false })
        }
      })
    }
  }

  generateTableDataRows = (dataRows) => {
    let rows = dataRows.map((row, i) => (
      <IdentityListTableDataRow
        data={row}
        condensed={this.props.choose}
        onClick={(identity) => this.setState({ identity })}
        key={i} />
    ))

    return rows
  }

  render() {
    if (this.props.loading) {
      return <Spinner className='slds-is-relative' size='small' />
    } else {
      let rows = this.generateTableDataRows(this.props.identities.edges)
      if (rows.length === 0) {
        return (
          <div className="slds-p-around_large">
            {this.state.identity
              ? <span></span>
              : <div>
                  No identity results found for <b>{this.props.searchText}</b>.
                  {this.props.onAddNew
                    ? <span>
                        {` `}
                        <Button
                          type="reset"
                          className="slds-m-left_xx-small"
                          onClick={this.props.onAddNew}>
                          Add new
                        </Button>?
                      </span>
                    : <span></span>
                  }
                </div>
            }
          </div>
        )
      } else {
        return (
          <div>
            <IdentityListTable
              heading="Identities"
              headingIcon="groups"
              tableColumnHeader={<IdentityListTableHeaderRow condensed={this.props.choose} />}
              tableDataRows={rows}
              nextPage={this.handleNextPage}
              previousPage={this.handlePreviousPage}
              onFirstPage={this.state.onFirstPage}
              onLastPage={this.state.onLastPage}
              navigationButtons="header"
              fixedLayout={true}
            />
            {this.props.choose
              ? <div className="slds-align_absolute-center">
                  <Button
                    onClick={() => this.props.onClick(this.state.identity)}
                    disabled={!this.state.identity}>
                    Confirm choice
                  </Button>
                </div>
              : <span></span>
            }
          </div>
        )
      }
    }
  }
}

const IdentityListTableHeaderRow = (props) => (
  <tr className="slds-line-height--reset">
    <th className="slds-text-title--caps" scope="col">
      <span className="slds-truncate slds-th__action slds-p-left_large" title="Name">
        Name
      </span>
    </th>
    <th className="slds-text-title--caps" scope="col">
      <span className="slds-truncate" title="Identity number">ID number</span>
    </th>
    {/* <th className="slds-text-title--caps" scope="col">
      <span className="slds-truncate" title="Employer / Division">Employer</span>
    </th> */}
  </tr>
)

const IdentityListTableDataRow = (props) => {
  let row = props.data.node

  return (
    <tr className="slds-hint-parent">
      <th scope="row">
        <div className="slds-truncate" title={row.firstName+' '+row.lastName}>
          <Radio
            label={row.firstName+' '+row.lastName}
            onClick={() => props.onClick(row)}  // row = identity
          />
        </div>
      </th>
      <td role="gridcell">
        <div className="slds-truncate" title={row.identityValue}>
          {row.identityValue}
        </div>
      </td>
    </tr>
  )
}

const getIdentities = gql`query($searchText: String!, $cursor: String) {
    identities(search: $searchText, first: 5, after: $cursor) {
      edges {
        node {
          id
          firstName
          lastName
          otherName
          identityValue
          dateOfBirth
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

const getPreviousIdentities = gql`query($searchText: String!, $cursor: String) {
    identities(search: $searchText, last: 5, before: $cursor) {
      edges {
        node {
          id
          firstName
          lastName
          otherName
          identityValue
        	dateOfBirth
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const IdentitySearchQuery = graphql(getIdentities, {
  options: ({ searchText }) => ({
    variables: { searchText },
    fetchPolicy: "network-only"
  }),
  props: ({ data: { loading, identities, fetchMore } }) => ({
    loading,
    identities,
    loadNextClients: (searchText) => {
      return fetchMore({
        query: getIdentities,
        variables: {
          searchText: searchText,
          cursor: identities.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.identities.edges
          const pageInfo = fetchMoreResult.identities.pageInfo

          return {
            identities: {
              __typename: previousResult.identities.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    },
    loadPreviousClients: (searchText) => {
      return fetchMore({
        query: getPreviousIdentities,
        variables: {
          searchText: searchText,
          cursor: identities.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.identities.edges
          const pageInfo = fetchMoreResult.identities.pageInfo

          return {
            identities: {
              __typename: previousResult.identities.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    }
  }),
})

export default IdentitySearchQuery(IdentitySearch)



// WEBPACK FOOTER //
// ./src/components/identity-search/index.js